import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Modal } from '../../../../../shared/components/modal/modal';
import { CustomerDataModal } from './customer-data-modal/customer-data-modal';
import { CustomerLogoModal } from './customer-logo-modal/customer-logo-modal';
import { CustomerThemeModal } from './customer-theme-modal/customer-theme-modal';
import { ReportModelModal } from '../report-model/report-model-modal';
import { AuthService } from '../../../../../core/services/auth.service';
import { UserRole } from '../../../../../core/models/user.model';

@Component({
  selector: 'app-customer-edit',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, Modal],
  templateUrl: './edit.html',
  styleUrl: './edit.scss',
})
export class CustomerEdit {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly modalService = inject(NgbModal);
  private readonly authService = inject(AuthService);

  // TG-385: a ação de modelo de relatório fica disponível para Admin e Super.
  readonly isAdminOrSuper = computed(() => {
    const role = this.authService.currentUser()?.role;
    return role === UserRole.Admin || role === UserRole.Super;
  });

  /**
   * Recebe o ID por:
   *
   * modalRef.componentInstance.customerId.set(id);
   */
  readonly customerId = signal('');

  /**
   * Recebe o nome da empresa por:
   *
   * modalRef.componentInstance.customerName.set(name);
   *
   * O nome é utilizado na pré-visualização do logotipo.
   */
  readonly customerName = signal('');

  /**
   * Indica se algum dos submodais salvou alterações.
   */
  readonly hasUpdates = signal(false);

  /**
   * Abre o modal de edição dos dados da empresa.
   */
  openCustomerDataModal(): void {
    const clientId = this.customerId().trim();

    if (!clientId) {
      console.error('CustomerEdit foi aberto sem customerId.');

      return;
    }

    const modalRef = this.modalService.open(CustomerDataModal, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.customerId.set(clientId);

    modalRef.result
      .then((result) => {
        if (!result?.updated) {
          return;
        }

        this.hasUpdates.set(true);

        /*
         * Caso o modal de dados devolva o novo nome,
         * atualiza o nome usado na pré-visualização
         * do modal de logotipo.
         */
        const updatedName = result?.customer?.name ?? result?.customer?.nome ?? result?.name ?? result?.nome;

        if (typeof updatedName === 'string' && updatedName.trim()) {
          this.customerName.set(updatedName.trim());
        }
      })
      .catch(() => {
        // Submodal fechado sem salvar.
      });
  }

  /**
   * Abre o modal de personalização do tema e das cores.
   */
  openCustomerThemeModal(): void {
    const clientId = this.customerId().trim();

    if (!clientId) {
      console.error('CustomerEdit foi aberto sem customerId.');

      return;
    }

    const modalRef = this.modalService.open(CustomerThemeModal, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.customerId.set(clientId);

    modalRef.result
      .then((result) => {
        if (result?.updated) {
          this.hasUpdates.set(true);
        }
      })
      .catch(() => {
        // Submodal fechado sem salvar.
      });
  }

  /**
   * Abre o modal de envio, substituição ou remoção
   * do logotipo da empresa.
   */
  openCustomerLogoModal(): void {
    const clientId = this.customerId().trim();

    if (!clientId) {
      console.error('CustomerEdit foi aberto sem customerId.');

      return;
    }

    const modalRef = this.modalService.open(CustomerLogoModal, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      fullscreen: 'md',
      modalDialogClass: 'modal-dialog-scrollable',
    });

    modalRef.componentInstance.customerId.set(clientId);

    modalRef.componentInstance.customerName.set(this.customerName().trim());

    modalRef.result
      .then((result) => {
        if (result?.updated) {
          this.hasUpdates.set(true);
        }
      })
      .catch(() => {
        // Submodal fechado sem salvar.
      });
  }

  /**
   * Abre o modal de seleção do modelo de relatório (TG-386).
   */
  openReportModelModal(): void {
    const clientId = this.customerId().trim();

    if (!clientId) {
      console.error('CustomerEdit foi aberto sem customerId.');

      return;
    }

    const modalRef = this.modalService.open(ReportModelModal, {
      fullscreen: true,
      backdrop: 'static',
      centered: true,
    });

    modalRef.componentInstance.clientId.set(clientId);

    modalRef.result
      .then((result) => {
        if (result) {
          this.hasUpdates.set(true);
        }
      })
      .catch(() => {
        // Submodal fechado sem salvar.
      });
  }

  /**
   * Fecha o modal principal e informa ao componente
   * chamador se houve alguma alteração.
   */
  close(): void {
    this.activeModal.close({
      updated: this.hasUpdates(),
    });
  }
}
